import { z } from 'zod';
import { listQuerySchema } from '../common/pagination';

/** Fertigungsauftrag (Modul production, ADR 0008). */
export const productionOrderStatusSchema = z.enum([
  'PLANNED',
  'RELEASED',
  'IN_PROGRESS',
  'DONE',
  'CANCELLED',
]);
export type ProductionOrderStatus = z.infer<typeof productionOrderStatusSchema>;

export const createProductionOrderSchema = z.object({
  articleId: z.string().uuid(),
  quantity: z.number().positive(),
  projectId: z.string().uuid().optional(),
  plannedStart: z.string().datetime().optional(),
  plannedEnd: z.string().datetime().optional(),
  notes: z.string().trim().max(5000).optional(),
});
export type CreateProductionOrderInput = z.infer<typeof createProductionOrderSchema>;

export const updateProductionOrderSchema = z.object({
  status: productionOrderStatusSchema.optional(),
  /** Nur im Status PLANNED änderbar. */
  quantity: z.number().positive().optional(),
  projectId: z.string().uuid().nullable().optional(),
  plannedStart: z.string().datetime().nullable().optional(),
  plannedEnd: z.string().datetime().nullable().optional(),
  notes: z.string().trim().max(5000).nullable().optional(),
});
export type UpdateProductionOrderInput = z.infer<typeof updateProductionOrderSchema>;

/** Listen-Query für Fertigungsaufträge — Standard-Query + Status-Filter. */
export const listProductionOrdersQuerySchema = listQuerySchema.extend({
  status: productionOrderStatusSchema.optional(),
});
export type ListProductionOrdersQuery = z.infer<typeof listProductionOrdersQuerySchema>;

/**
 * Query der Fertigmeldung: `?force=true` bucht trotz Material-Unterdeckung
 * (Query-Parameter kommen als String an, daher kein z.boolean()).
 */
export const completeProductionOrderQuerySchema = z.object({
  force: z.enum(['true', 'false']).optional(),
});
export type CompleteProductionOrderQuery = z.infer<typeof completeProductionOrderQuerySchema>;

/**
 * Materialbedarf einer Stücklisten-Komponente für einen Fertigungsauftrag,
 * inkl. Verfügbarkeit: `available` = Bestand − Reservierungen ANDERER
 * Verbraucher; `shortage` = fehlende Menge für diesen Auftrag.
 */
export const productionOrderComponentSchema = z.object({
  componentArticleId: z.string().uuid(),
  componentNumber: z.string(),
  componentName: z.string(),
  unit: z.string().nullable(),
  /** Menge je gefertigter Einheit (aus der Stückliste). */
  quantityPer: z.number(),
  /** Gesamtbedarf = quantityPer × Auftragsmenge. */
  required: z.number(),
  /** Aktueller Lagerbestand der Komponente. */
  stockQty: z.number(),
  /** Für DIESEN Fertigungsauftrag aktiv reservierte Menge. */
  reservedForOrder: z.number(),
  /** Verfügbar für diesen Auftrag = Bestand − fremde aktive Reservierungen. */
  available: z.number(),
  /** Unterdeckung = max(required − available, 0). */
  shortage: z.number(),
});
export type ProductionOrderComponent = z.infer<typeof productionOrderComponentSchema>;

export const productionOrderResponseSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  number: z.string(),
  articleId: z.string().uuid(),
  articleNumber: z.string(),
  articleName: z.string(),
  quantity: z.number(),
  status: productionOrderStatusSchema,
  projectId: z.string().uuid().nullable(),
  projectCode: z.string().nullable(),
  plannedStart: z.string().datetime().nullable(),
  plannedEnd: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type ProductionOrderResponse = z.infer<typeof productionOrderResponseSchema>;
